import React, { useEffect, useState, useMemo } from 'react'
import { fetchCmsItems } from '../lib/cms'
import TripCard from '../components/TripCard'

export default function TripsPage() {
  const [trips, setTrips] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLand, setSelectedLand] = useState('alle')
  const [selectedDestination, setSelectedDestination] = useState('alle')
  const [selectedRejsetype, setSelectedRejsetype] = useState('alle')
  const [sortBy, setSortBy] = useState('afrejse')

  useEffect(() => {
    document.title = 'Skirejser - SKI & CO TRAVEL ApS'
    async function loadTrips() {
      const data = await fetchCmsItems('rejser')
      setTrips(data)
    }
    loadTrips()
  }, [])

  // Filter & Sort Logic
  const filteredTrips = useMemo(() => {
    let result = [...trips]

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter(
        (t) =>
          t.rejsenavn?.toLowerCase().includes(term) ||
          t.destination?.toLowerCase().includes(term) ||
          t.land?.toLowerCase().includes(term) ||
          t.kort_beskrivelse?.toLowerCase().includes(term)
      )
    }

    if (selectedLand !== 'alle') {
      result = result.filter((t) => t.land === selectedLand)
    }

    if (selectedDestination !== 'alle') {
      result = result.filter((t) => t.destination === selectedDestination)
    }

    if (selectedRejsetype !== 'alle') {
      result = result.filter((t) => t.rejsetype === selectedRejsetype)
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'pris-lav') return (a.pris || 0) - (b.pris || 0)
      if (sortBy === 'pris-hoej') return (b.pris || 0) - (a.pris || 0)
      if (sortBy === 'destination') return (a.destination || '').localeCompare(b.destination || '')
      // Default: afrejsedato
      return (a.afrejsedato || '').localeCompare(b.afrejsedato || '')
    })

    return result
  }, [trips, searchTerm, selectedLand, selectedDestination, selectedRejsetype, sortBy])

  const handleResetFilters = () => {
    setSearchTerm('')
    setSelectedLand('alle')
    setSelectedDestination('alle')
    setSelectedRejsetype('alle')
    setSortBy('afrejse')
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-10">
      {/* Page Header */}
      <div className="space-y-3">
        <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
          SKI & CO TRAVEL ApS
        </span>
        <h1 className="font-heading font-extrabold text-2xl sm:text-4xl text-white">
          Skirejser & Oplevelser
        </h1>
        <p className="text-slate-300 text-sm sm:text-base max-w-2xl leading-relaxed">
          Gå på opdagelse i vores aktuelle skirejser til Østrig, Italien, Frankrig og Norge. Brug filtrene nedenfor til at finde den perfekte skiferie.
        </p>
      </div>

      {/* Filter Bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 sm:p-6 shadow-xl space-y-4">
        {/* Search Row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-6 relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" aria-hidden="true" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Søg på rejse, destination eller hotel..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
            />
          </div>

          <div className="md:col-span-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors"
            >
              <option value="afrejse">Sorter: Nærmeste afrejse</option>
              <option value="pris-lav">Sorter: Pris, laveste først</option>
              <option value="pris-hoej">Sorter: Pris, højeste først</option>
              <option value="destination">Sorter: Destination</option>
            </select>
          </div>

          <div className="md:col-span-3 flex items-center justify-end">
            <button
              onClick={handleResetFilters}
              className="w-full py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-colors flex items-center justify-center gap-2 border border-slate-700"
            >
              <i className="fa-solid fa-rotate-left" aria-hidden="true" />
              <span>Nulstil filtre</span>
            </button>
          </div>
        </div>

        {/* Dropdown Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-800/80">
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">
              Land
            </label>
            <select
              value={selectedLand}
              onChange={(e) => setSelectedLand(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-cyan-400"
            >
              <option value="alle">Alle lande</option>
              <option value="Østrig">Østrig</option>
              <option value="Italien">Italien</option>
              <option value="Frankrig">Frankrig</option>
              <option value="Norge">Norge</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">
              Destination
            </label>
            <select
              value={selectedDestination}
              onChange={(e) => setSelectedDestination(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-cyan-400"
            >
              <option value="alle">Alle destinationer</option>
              <option value="St. Anton am Arlberg">St. Anton am Arlberg</option>
              <option value="Saalbach Hinterglemm">Saalbach Hinterglemm</option>
              <option value="Val Gardena / Dolomitterne">Val Gardena / Dolomitterne</option>
              <option value="Val Thorens">Val Thorens</option>
              <option value="Hemsedal">Hemsedal</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">
              Rejsetype
            </label>
            <select
              value={selectedRejsetype}
              onChange={(e) => setSelectedRejsetype(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-cyan-400"
            >
              <option value="alle">Alle rejsetyper</option>
              <option value="Luksus & Wellness">Luksus & Wellness</option>
              <option value="Familie skiferie">Familie skiferie</option>
              <option value="Gourmet & Livsnyder">Gourmet & Livsnyder</option>
              <option value="Off-piste & Gruppeniveau">Off-piste & Gruppeniveau</option>
              <option value="Hytteferie & Fjeld">Hytteferie & Fjeld</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Header */}
      <div className="flex items-center justify-between text-sm text-slate-400 font-medium">
        <div>
          Viser <span className="text-white font-bold">{filteredTrips.length}</span> ud af{' '}
          <span className="text-white font-bold">{trips.length}</span> skirejser
        </div>
      </div>

      {/* Grid or Empty State */}
      {filteredTrips.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTrips.map((trip) => (
            <TripCard key={trip.id} trip={trip} />
          ))}
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-slate-800 text-slate-400 flex items-center justify-center mx-auto text-2xl">
            <i className="fa-solid fa-mountain-sun" aria-hidden="true" />
          </div>
          <h3 className="font-heading font-bold text-xl text-white">
            {trips.length === 0
              ? 'Alle vores skirejser skræddersys individuelt'
              : 'Ingen skirejser matchede din søgning'}
          </h3>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            {trips.length === 0
              ? 'Vi sammensætter unikke skirejser tilpasset jeres gruppes behov, ønsker og budget. Kontakt os i dag for et uforpligtende rejseforslag.'
              : 'Prøv at nulstille dine filtre eller søge på andre ord. Du er også velkommen til at kontakte os direkte for et tilbud.'}
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            {trips.length === 0 ? (
              <>
                <a
                  href="/kontakt"
                  className="px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>Indhent tilbud</span>
                </a>
                <a
                  href="/destinationer"
                  className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-2 border border-slate-700"
                >
                  <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
                  <span>Se destinationer</span>
                </a>
              </>
            ) : (
              <button
                onClick={handleResetFilters}
                className="px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-2"
              >
                <i className="fa-solid fa-rotate-left" aria-hidden="true" />
                <span>Nulstil søgning</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
